import { Scene } from 'phaser';
import { MissionConfig } from '../types';
import { LiftStatus, SimulationState, FloorState, LiftState } from '../types/simulationState';

import { EventBus } from '../EventBus';

interface DoorGameObjects {
    label: Phaser.GameObjects.Text;
    door: Phaser.GameObjects.Sprite;
}

interface FloorsGameObject {
    doors: Record<string, DoorGameObjects>;
    visitorStats: Phaser.GameObjects.Text;
    floorPanelButton: Phaser.GameObjects.Text;
    visitorIds: string[];
}

interface LiftGameObject {
    lift: Phaser.GameObjects.Sprite;
    directionIndicator: Phaser.GameObjects.Text;
    targetY: number | null;
    nextFloorNumber: number | null;
    passengerIds: string[];
    stats: {
        idText: Phaser.GameObjects.Text;
        weightText: Phaser.GameObjects.Text;
        passengerText: Phaser.GameObjects.Text;
    }
}

export interface LiftMoveCompletedEvent {
    liftId: string;
    newFloorNumber: number;
}

export interface FloorPanelClickedEvent {
    floorNumber: number;
}

export interface LiftPanelClickedEvent {
    liftId: string;
}

export class UIEvents {
    static readonly FLOOR_PANEL_CLICKED = 'FLOOR_PANEL_CLICKED';
    static readonly LIFT_PANEL_CLICKED = 'LIFT_PANEL_CLICKED';
    static readonly LIFT_MOVE_COMPLETED = 'LIFT_MOVE_COMPLETED';
};

export let g_ActiveSimulationScene: LiftSimulationScene | null = null;

export class LiftSimulationScene extends Scene {

    private readonly DOOR_FRAME_DOOR_OPENED = 6;
    private readonly DOOR_FRAME_DOOR_CLOSED = 0;
    private readonly DOOR_WIDTH = 32;
    private readonly DOOR_HEIGHT = 48;
    private readonly DOOR_LABEL_SPACING = 5
    private readonly DOOR_PANEL_SPACING = 5
    private readonly DOOR_PANEL_WIDTH = 10;
    private readonly DOOR_LABEL_HEIGHT = 10;
    // Door container:
    // Top: label
    // Bottom: door
    // Right: panel button
    private readonly DOOR_CONTAINER_WIDTH = this.DOOR_WIDTH + this.DOOR_PANEL_SPACING + this.DOOR_PANEL_WIDTH;
    private readonly DOOR_CONTAINER_HEIGHT = this.DOOR_LABEL_HEIGHT + this.DOOR_LABEL_SPACING + this.DOOR_HEIGHT;
    private readonly FLOOR_BORDER_WIDTH = 4;
    private readonly FLOOR_LABEL_WIDTH = 20;
    private readonly FLOOR_HEIGHT = this.DOOR_CONTAINER_HEIGHT + 2 * this.FLOOR_BORDER_WIDTH;

    private floors: Record</*floorNumber*/number, FloorsGameObject> = {};
    private lifts: Record</*liftId*/string, LiftGameObject> = {};
    private missionConfig: MissionConfig | null = null;
    private totalVisitorsText: Phaser.GameObjects.Text | null = null;
    private transportedVisitorsText: Phaser.GameObjects.Text | null = null;
    private hadSimulationState: boolean = false;
    private isDirty: boolean = false;
    private floorInitY: number = 0;

    constructor() {
        super('LiftSimulationScene');
        g_ActiveSimulationScene = this as LiftSimulationScene;
    }

    preload(): void {
        this.load.spritesheet('door', 'assets/sprites/door_spritesheet.png', { frameWidth: this.DOOR_WIDTH, frameHeight: this.DOOR_HEIGHT, endFrame: this.DOOR_FRAME_DOOR_OPENED });
    }

    create(): void {
        this.missionConfig = this.readMissionConfig();
        this.resetSimulation();
    }

    dispose(): void {
        if (g_ActiveSimulationScene === this) {
            g_ActiveSimulationScene = null;
        }
    }

    private readSimulationState(): SimulationState | null {
        return this.registry.get('simulation') as SimulationState;
    }

    private readMissionConfig(): MissionConfig | null {
        return this.registry.get('missionConfig') as MissionConfig;
    }

    update(_time: number, delta: number): void {
        const simulationState = this.readSimulationState();
        if (simulationState == null) {
            if (this.hadSimulationState) {
                this.hadSimulationState = false;
                this.resetSimulation();
            }
            return;
        }
        this.hadSimulationState = true;

        if (this.isDirty) {
            this.isDirty = false;
            this.renderBuildingState(simulationState);

            // Always route lifts towards the first target
            for (const [liftId, liftState] of Object.entries(simulationState.building.lifts) as [string, LiftState][]) {
                const liftObj = this.lifts[liftId];
                if (!liftObj) continue;
                const targetedFloor = liftState.targetedFloors[0] || liftState.currentFloor;
                const newTargetY = this.getFloorDoorPosition(targetedFloor, liftId);
                if (liftObj.targetY === newTargetY) continue;
                if (liftObj.targetY === null && liftState.status === LiftStatus.IDLE) continue;

                liftObj.targetY = newTargetY;
                liftObj.nextFloorNumber = targetedFloor;
            }
        }

        // Handle linear lift movement
        for (const [liftId, liftObj] of Object.entries(this.lifts)) {
            const liftConfig = this.missionConfig?.lifts[liftId];
            const numFloors = this.missionConfig?.floors.length || 0;


            if (liftObj.targetY !== null) {
                const speed = this.FLOOR_HEIGHT / (liftConfig?.liftMoveSpeedSeconds || 2); // pixels per second
                const moveAmount = speed * (delta / 1000);

                const currentY = liftObj.lift.y;
                const diff = liftObj.targetY - currentY;

                if (Math.abs(diff) <= moveAmount) {
                    liftObj.lift.y = liftObj.targetY;
                    const arrivedFloor = liftObj.nextFloorNumber!;
                    liftObj.targetY = null;
                    liftObj.nextFloorNumber = null;

                    EventBus.get().emit(UIEvents.LIFT_MOVE_COMPLETED, {
                        liftId: liftId,
                        newFloorNumber: arrivedFloor
                    } as LiftMoveCompletedEvent);
                } else {
                    liftObj.lift.y += Math.sign(diff) * moveAmount;
                }
            }

            // Update physical-position-based displayed floor
            const liftY = liftObj.lift.y;
            const relativeY = liftY - this.floorInitY;
            const rawFloor = (numFloors - 1) - (relativeY / this.FLOOR_HEIGHT);
            const displayedFloor = Math.round(rawFloor);

            // update lift shaft labels for this lift
            for (const floorObject of Object.values(this.floors)) {
                const doorObjects = floorObject.doors[liftId];
                if (doorObjects) {
                    doorObjects.label.setText(displayedFloor.toString());
                }
            }
        }
    }

    markDirty(): void {
        this.isDirty = true;
    }

    private resetSimulation(): void {
        if (!this.missionConfig) return;

        // Destroy all existing game objects
        if (this.totalVisitorsText) {
            this.totalVisitorsText.destroy();
            this.totalVisitorsText = null;
        }
        if (this.transportedVisitorsText) {
            this.transportedVisitorsText.destroy();
            this.transportedVisitorsText = null;
        }

        // Destroy Floors
        for (const floorObj of Object.values(this.floors)) {
            floorObj.visitorStats.destroy();
            floorObj.floorPanelButton.destroy();
            for (const doorObj of Object.values(floorObj.doors)) {
                doorObj.label.destroy();
                doorObj.door.destroy();
            }
        }
        this.floors = {};

        // Destroy Lifts
        for (const liftObj of Object.values(this.lifts)) {
            liftObj.lift.destroy();
            liftObj.directionIndicator.destroy();
            liftObj.stats.idText.destroy();
            liftObj.stats.weightText.destroy();
            liftObj.stats.passengerText.destroy();
        }
        this.lifts = {};

        // Re-render building and lifts
        this.renderBuilding(this.missionConfig);
        for (const liftId of Object.keys(this.missionConfig.lifts)) {
            this.renderLift(this.missionConfig, liftId);
        }
    }



    private renderBuildingState(simulationState: SimulationState): void {
        const buildingState = simulationState.building;

        // Update total building visitors
        if (this.totalVisitorsText) {
            const totalVisitors = Object.keys(buildingState.visitors).length;
            this.totalVisitorsText.setText(`Current 👤: ${totalVisitors}`);
        }

        // Update transported visitors
        if (this.transportedVisitorsText) {
            const completedCount = simulationState.completedTrips.length;
            this.transportedVisitorsText.setText(`Transported 👤: ${completedCount}`);
        }

        // Update floor visitor stats
        for (const [floorNum, floorData] of Object.entries(buildingState.floors) as [string, FloorState][]) {
            const floorNumber = parseInt(floorNum);
            const floorObj = this.floors[floorNumber];
            if (floorObj) {
                const count = floorData.visitors.length;
                floorObj.visitorStats.setText(`👤 ${count}`);
            }
        }

        // Update lift info
        for (const [liftId, liftState] of Object.entries(buildingState.lifts) as [string, LiftState][]) {
            const liftObj = this.lifts[liftId];
            if (!liftObj) continue;


            // Update Direction Indicator
            let dirText = '−';
            const firstTarget = liftState.targetedFloors[0];
            if (firstTarget !== undefined) {
                if (firstTarget > liftState.currentFloor) dirText = '▲';
                else if (firstTarget < liftState.currentFloor) dirText = '▼';
            }
            liftObj.directionIndicator.setText(dirText);

            // Update header stats
            const passengerCount = liftState.passengers.length;

            let currentWeight = 0;
            liftState.passengers.forEach(pId => {
                const visitor = buildingState.visitors[pId];
                if (visitor) {
                    currentWeight += visitor.weight;
                }
            });

            // Max weight is in MissionConfig, but we don't have easy access to it here unless we store it or look it up.
            // We have this.missionConfig.
            const maxWeight = this.missionConfig?.lifts[liftId]?.weightLimit || 0;

            liftObj.stats.weightText.setText(`⚖️ ${currentWeight}/${maxWeight}`);
            liftObj.stats.passengerText.setText(`👤 ${passengerCount}`);
        }

        this.animateVisitors(simulationState);
    }

    private animateVisitors(simulationState: SimulationState): void {
        const buildingState = simulationState.building;

        // 0. Gather all current IDs for comparison
        const previousAllLiftPassengerIds = Object.values(this.lifts).flatMap(l => l.passengerIds || []);

        // 1. Detect Arrivals and Loading on Floors
        for (const [floorNum, floorData] of Object.entries(buildingState.floors) as [string, FloorState][]) {
            const floorNumber = parseInt(floorNum);
            const floorObj = this.floors[floorNumber];
            if (!floorObj) continue;

            const previousVisitors = floorObj.visitorIds || [];
            const currentVisitors = floorData.visitors;

            // Arrivals (New to the floor)
            const floorArrivedIds = currentVisitors.filter(vId => !previousVisitors.includes(vId));
            for (const vId of floorArrivedIds) {
                // If they weren't in any lift previously, they arrived from outside
                if (!previousAllLiftPassengerIds.includes(vId)) {
                    this.createVisitorTween(
                        { x: this.game.scale.width, y: floorObj.visitorStats.y },
                        { x: floorObj.visitorStats.x, y: floorObj.visitorStats.y }
                    );
                }
            }

            // Departures (Gone from floor)
            const floorDepartedIds = previousVisitors.filter(vId => !currentVisitors.includes(vId));
            for (const vId of floorDepartedIds) {
                // Check if they moved to a lift
                for (const [liftId, liftState] of Object.entries(buildingState.lifts) as [string, LiftState][]) {
                    if (liftState.passengers.includes(vId)) {
                        const liftObj = this.lifts[liftId];
                        if (liftObj) {
                            this.createVisitorTween(
                                { x: floorObj.visitorStats.x, y: floorObj.visitorStats.y },
                                { x: liftObj.stats.passengerText.x, y: liftObj.stats.passengerText.y }
                            );
                        }
                        break;
                    }
                }
            }
            floorObj.visitorIds = [...currentVisitors];
        }

        // 2. Detect Unloading from Lifts
        for (const [liftId, liftState] of Object.entries(buildingState.lifts) as [string, LiftState][]) {
            const liftObj = this.lifts[liftId];
            if (!liftObj) continue;

            const previousPassengers = liftObj.passengerIds || [];
            const currentPassengers = liftState.passengers;
            const departedPassengers = previousPassengers.filter(pId => !currentPassengers.includes(pId));

            if (departedPassengers.length > 0) {
                for (const pId of departedPassengers) {
                    // Check if they are on any floor now
                    let foundOnFloor = false;
                    for (const fData of Object.values(buildingState.floors)) {
                        if (fData.visitors.includes(pId)) {
                            foundOnFloor = true;
                            break;
                        }
                    }

                    if (!foundOnFloor) {
                        // Truly left the simulation (transported)
                        if (this.transportedVisitorsText) {
                            this.createVisitorTween(
                                { x: liftObj.stats.passengerText.x, y: liftObj.stats.passengerText.y },
                                { x: this.transportedVisitorsText.x, y: this.transportedVisitorsText.y }
                            );
                        }
                    }
                }
            }
            liftObj.passengerIds = [...currentPassengers];
        }
    }

    private createVisitorTween(from: { x: number, y: number }, to: { x: number, y: number }): void {
        const sprite = this.add.text(from.x, from.y, '👤', { fontSize: '14px' });
        sprite.setDepth(1000);
        this.tweens.add({
            targets: sprite,
            x: to.x,
            y: to.y,
            duration: 750,
            ease: 'Power1',
            onComplete: () => {
                sprite.destroy();
            }
        });
    }


    private getFloorDoorPosition(floorNumber: number, liftId: string): number {
        const doorData = this.floors[floorNumber]?.doors[liftId];
        if (!doorData) return -1;
        return doorData.door.y;
    }

    private renderLift(missionConfig: MissionConfig, liftId: string): void {
        const liftConfig = missionConfig.lifts[liftId];
        if (!liftConfig) return;

        const doorData = this.floors[liftConfig.startingFloor]?.doors[liftId];
        if (!doorData) return;

        const lift = this.add.sprite(doorData.door.x, doorData.door.y, 'door', this.DOOR_FRAME_DOOR_OPENED);
        lift.setOrigin(0);
        lift.setDepth(100);

        const FLOOR_INIT_Y = (this.game.scale.height - this.FLOOR_HEIGHT * missionConfig.floors.length) / 2;

        // Header Stats
        const statsY = FLOOR_INIT_Y - 80;
        const idText = this.add.text(doorData.door.x + this.DOOR_WIDTH / 2, statsY, liftId, {
            fontSize: '16px',
            fontFamily: 'Arial',
            color: '#000000',
            align: 'center'
        }).setOrigin(0.5, 1);

        const weightText = this.add.text(doorData.door.x + this.DOOR_WIDTH / 2, statsY + 25, `⚖️ 0/${liftConfig.weightLimit}`, {
            fontSize: '12px',
            fontFamily: 'Arial',
            color: '#333333',
            align: 'center'
        }).setOrigin(0.5, 1);

        const passengerText = this.add.text(doorData.door.x + this.DOOR_WIDTH / 2, statsY + 45, "👤 0", {
            fontSize: '12px',
            fontFamily: 'Arial',
            color: '#333333',
            align: 'center'
        }).setOrigin(0.5, 1);

        const directionIndicator = this.add.text(doorData.door.x + this.DOOR_WIDTH / 2, statsY + 70, "−", {
            fontSize: '15px',
            fontFamily: 'Arial',
            color: '#FF00FF',
            align: 'left'
        }).setOrigin(0.5, 1);

        const panelButton = this.add.text(doorData.door.x + this.DOOR_WIDTH / 2, statsY - 25,
            "⚙️", {
            fontSize: '12px',
            fontFamily: 'Arial',
            color: '#000000',
            align: 'center'
        });
        panelButton.setOrigin(0.5, 1);
        panelButton.setInteractive()
        panelButton.on('pointerdown', () => {
            EventBus.get().emit(UIEvents.LIFT_PANEL_CLICKED, {
                liftId: liftId
            } as LiftPanelClickedEvent);
        });

        this.lifts[liftId] = {
            lift: lift,
            directionIndicator: directionIndicator,
            targetY: null,
            nextFloorNumber: null,
            passengerIds: [],
            stats: {
                idText: idText,
                weightText: weightText,
                passengerText: passengerText
            }
        };
    }

    private renderBuilding(missionConfig: MissionConfig): void {
        // columns = # of lifts
        // rows = # of floors
        const FLOOR_WIDTH = this.game.scale.width - 100 * 2;
        const FLOOR_INIT_X = 100;
        // center the floors within the canvas
        this.floorInitY = (this.game.scale.height - this.FLOOR_HEIGHT * missionConfig.floors.length) / 2;
        const FLOOR_INIT_Y = this.floorInitY;

        const liftIds = Object.keys(missionConfig.lifts) as string[];
        const numDoors = liftIds.length;
        const numFloors = missionConfig.floors.length;

        // center the door containers within the floor
        const doorContainerSpacing = (FLOOR_WIDTH - 2 * this.FLOOR_BORDER_WIDTH) / (numDoors * this.DOOR_CONTAINER_WIDTH);
        const doorContainerInitX = FLOOR_INIT_X + doorContainerSpacing / 2 + this.FLOOR_BORDER_WIDTH;

        // render grid from top to bottom, top = top floor, bottom = bottom floor
        for (let row = 0; row < numFloors; row++) {
            const floorNumber = numFloors - row - 1;

            // Render floor background
            const floorPosY = FLOOR_INIT_Y + row * this.FLOOR_HEIGHT;
            const floorBg = this.add.rectangle(FLOOR_INIT_X, floorPosY, FLOOR_WIDTH, this.FLOOR_HEIGHT, 0xd3d3d3);
            floorBg.setOrigin(0, 0);
            floorBg.setStrokeStyle(this.FLOOR_BORDER_WIDTH, 0xefc53f); // Parameters: lineWidth, strokeColor (hex), strokeAlpha (optional)

            const floorLabel = this.add.text(FLOOR_INIT_X - this.FLOOR_BORDER_WIDTH - this.FLOOR_LABEL_WIDTH, floorPosY + this.FLOOR_HEIGHT / 2 - 11,
                floorNumber.toString(), {
                fontSize: '22px',
                fontFamily: 'Arial',
                color: '#000000',
            });
            floorLabel.setOrigin(0, 0);

            const floorStructure: Partial<FloorsGameObject> = {
                doors: {},
                visitorIds: []
            };
            this.floors[floorNumber] = floorStructure as FloorsGameObject;

            // Visitor Stats
            const visitorStats = this.add.text(FLOOR_INIT_X + FLOOR_WIDTH - 20, floorPosY + this.FLOOR_HEIGHT / 2, "👤 0", {
                fontSize: '14px',
                fontFamily: 'Arial',
                color: '#000000',
            });
            visitorStats.setOrigin(1, 0.5);
            floorStructure.visitorStats = visitorStats;

            // Render doors by spreading them equidistant at the center of the floor            
            for (let col = 0; col < liftIds.length; col++) {
                const liftId = liftIds[col];
                if (!liftId) continue;
                const liftConfig = missionConfig.lifts[liftId];
                if (!liftConfig) continue;
                // check if lift covers this floor
                if (!(
                    liftConfig.bottomFloor <= floorNumber
                    && liftConfig.topFloor >= floorNumber
                )) {
                    continue;
                }

                const doorContainerX = doorContainerInitX + col * (this.DOOR_CONTAINER_WIDTH + 2 * doorContainerSpacing);
                const doorContainerY = floorPosY + this.FLOOR_BORDER_WIDTH * 2;

                // const doorContainerBg = this.add.rectangle(doorContainerX, doorContainerY, this.DOOR_CONTAINER_WIDTH, this.DOOR_CONTAINER_HEIGHT, 0xFF66ff);
                // doorContainerBg.setOrigin(0);

                const doorLabel = this.add.text(doorContainerX + this.DOOR_CONTAINER_WIDTH / 2 - (this.DOOR_PANEL_SPACING + this.DOOR_PANEL_WIDTH) / 2 - 2, doorContainerY, "0", {
                    fontSize: '12px',
                    fontFamily: 'Arial',
                    color: '#000000',
                });

                const door = this.add.sprite(doorContainerX, doorContainerY + this.DOOR_CONTAINER_HEIGHT - this.DOOR_HEIGHT, 'door', this.DOOR_FRAME_DOOR_CLOSED);
                door.setOrigin(0)
                door.setDepth(10);

                floorStructure.doors![liftId] = {
                    label: doorLabel,
                    door: door,
                } as DoorGameObjects;
            }

            // One floor panel button per floor (next to floor label)
            const floorPanelButton = this.add.text(FLOOR_INIT_X - this.FLOOR_BORDER_WIDTH - 5, floorPosY + this.FLOOR_HEIGHT / 2 + 11,
                "⚙️", {
                fontSize: '14px',
                fontFamily: 'Arial',
                color: '#000000',
            });
            floorPanelButton.setOrigin(1, 0);
            floorPanelButton.setInteractive();
            floorPanelButton.on('pointerdown', () => {
                EventBus.get().emit(UIEvents.FLOOR_PANEL_CLICKED, {
                    floorNumber: floorNumber,
                } as FloorPanelClickedEvent);
            });
            floorStructure.floorPanelButton = floorPanelButton;
        }

        // Total Stats
        this.totalVisitorsText = this.add.text(10, this.game.scale.height - 50, "Current 👤: 0", {
            fontSize: '16px',
            fontFamily: 'Arial',
            color: '#000000',
        });

        this.transportedVisitorsText = this.add.text(10, this.game.scale.height - 25, "Transported 👤: 0", {
            fontSize: '16px',
            fontFamily: 'Arial',
            color: '#000000',
        });

        // this.anims.create({
        //     key: 'door.open',
        //     frames: this.anims.generateFrameNumbers('door', { start: 0, end: 6 }),
        //     frameRate: 12,
        //     repeat: 0
        // });
    }

    // private playOpenDoorAnimation(floorNumber: number, liftId: string, shouldOpen: boolean = true): void {
    //     const floorStructure = this.floors[floorNumber];
    //     if (!floorStructure) return;
    //     const doorStructure = floorStructure.doors[liftId];
    //     if (!doorStructure) return;
    //     if (shouldOpen) doorStructure.door.play('door.open'); // open door animation
    //     else doorStructure.door.playReverse('door.open'); // close door animation
    // }
}